/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * TabManager - Multi-Tab Code Editor with Block Management
 *
 * Sophisticated tabbed code editor supporting multiple code tabs, real-time syntax
 * validation, block creation/editing, step execution, and context menu operations.
 *
 * Architecture:
 * ┌─────────────────────────────────────────┐
 * │ TabManager (Context Provider)           │
 * │ - Creates shared state context          │
 * │ - Manages core state (tabs, cache)      │
 * │ └─────────────────────────────────────┐ │
 * │   TabManagerInternal (Consumer)       │ │
 * │   - Contains all UI logic             │ │
 * │   - Orchestrates hooks                │ │
 * │   - Handles user interactions         │ │
 * │   └─────────────────────────────────┘ │ │
 * └─────────────────────────────────────────┘
 *

 */

import * as monaco from 'monaco-editor';
import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef, useCallback } from 'react';

import { TabManagerProvider, useTabManagerContext } from './context/TabManagerContext';
import { hasOneRunningStepOrOneInQueue, isSyntaxValid } from './helpers/codeEditorHelpers';
import { CachedTab } from './hooks/types';
import { useBlockCache } from './hooks/useBlockCache';
import { useBlockOperations } from './hooks/useBlockOperations';
import { useBlockRename } from './hooks/useBlockRename';
import { useEditorEvents } from './hooks/useEditorEvents';
import { useEditorIntegration } from './hooks/useEditorIntegration';
import { useStoryInfo, StoryInfo, TabInfo } from './hooks/useStoryInfo';
import { useTabOperations } from './hooks/useTabOperations';
import { useTabState } from './hooks/useTabState';
import { AstParser } from './managers/AstParser';
import { DecorationManager } from './managers/DecorationManager';
import { TabBar } from './TabBar';
import { FunctionMetadata } from './types/LiveAgentEditorTypes';

import { Step as UIStep, Story } from '@/pages/projects/LiveAgentTypes';

/** TabManager component props */
interface TabManagerProps {
    /** Initial code content for the main tab */
    initialCode: string;
    /** Reference to Monaco editor instance */
    editorRef: React.MutableRefObject<monaco.editor.IStandaloneCodeEditor | undefined>;
    /** AST parser for syntax analysis */
    astParser: AstParser;
    /** Current story steps for execution and highlighting */
    steps: UIStep[];
    /** Whether editor interactions are disabled */
    isDisabled?: boolean;
    /** Whether to hide Playwright-specific UI elements */
    hidePlaywright?: boolean;
    /** Variable mappings for code substitution */
    variables?: { [key: string]: string };
    /** Queue of steps pending execution */
    queue?: number[];
    /** Callback when a step should be executed */
    executeStep?: (step: UIStep) => void;
    /** Callback to show element selector interface */
    showSelector?: () => void;
    /** Callback to highlight elements in browser */
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    /** Callback when syntax errors are detected */
    onSyntaxError?: (hasError: boolean, messages: string[]) => void;
    /** Whether editor is in read-only mode */
    readOnly?: boolean;
    /** Block definitions for creating new tabs */
    blocksData?: Story[];
}

/** Imperative API exposed by TabManager via ref */
export interface TabManagerRef {
    /** Get complete story information including all steps */
    getStoryInfo: () => StoryInfo;
    /** Get information about a specific tab */
    getTabInfo: (tabKey: string) => TabInfo;
    /** Check if current code has valid syntax */
    isSyntaxValid: () => boolean;
    /** Initialize editor decorations and event handlers */
    onEditorReady: () => void;
    /** Get all cached block data */
    getCachedBlocks: () => Map<string, CachedTab>;
    /** Mark a tab as saved (clear modification indicators) */
    markTabAsSaved: (tabKey: string) => void;
    /** Check if a tab has been modified */
    isTabModified: (tabKey: string) => boolean;
    /** Get list of all modified tab IDs */
    getAllModifiedTabs: () => string[];
    /** Update steps for a specific block */
    dispatchBlockSteps: (blockName: string, blockSteps: UIStep[]) => boolean;
    /** Get the current active tab ID */
    getActiveTabId: () => string;
}

/**
 * TabManagerInternal - Core UI Component (Context Consumer)
 *
 * This component contains all the actual UI logic and user interaction handling.
 * It consumes the context provided by TabManager and orchestrates all the hooks.
 *
 * Responsibilities:
 * - Initialize and coordinate all feature hooks
 * - Handle user interactions (tab switching, closing, etc.)
 * - Set up editor event handlers
 * - Manage block rename modal
 * - Expose imperative API via ref
 */
// Internal component that uses the context
const TabManagerInternal = forwardRef<TabManagerRef, TabManagerProps>((props, ref) => {
    // === CONTEXT ACCESS ===
    // Get shared state and operations from context
    const context = useTabManagerContext();

    // === HOOK INITIALIZATION ===
    // Initialize all feature hooks with reduced parameter counts

    /** Editor integration: decorations, content management, change handling */
    const {
        applyDecorationsForTab,
        clearAllDecorations,
        applyTabContent,
        handleEditorChange,
        refreshMainTabMetadata,
        refreshCurrentTabDecorations,
    } = useEditorIntegration();

    /** Block operations: creation, caching, step management */
    const { openBlockTabFromMetadata, getBlockSteps, enrichBlockSteps, dispatchBlockSteps } = useBlockOperations();

    /** Block rename functionality: validation, operations */
    const { checkForBlockRename } = useBlockRename();

    /** Tab operations: switching, closing */
    const { switchTab, closeTab } = useTabOperations();

    /** Story information: step extraction, metadata */
    const { getStoryInfo, getTabInfo } = useStoryInfo();

    // === LOCAL STATE ===
    // Initialize hooks that manage local component state

    /** Force re-render trigger for tab updates */
    const [, forceUpdate] = useState({});
    const triggerRerender = () => forceUpdate({});

    /** Block cache operations for persistence */
    const { markTabAsSaved, isTabModified, getAllModifiedTabs } = useBlockCache();

    // === SET REFRESH DECORATIONS FUNCTION ===
    // Set the actual refresh function that the parent can call after updating refs
    // No useEffect needed - just set the ref directly since all captured values are stable
    if (context.refreshDecorationsRef) {
        context.refreshDecorationsRef.current = () => {
            // First, dispatch block step selectors to all tabs
            context.stepsRef.current.forEach((step) => {
                if (step.kind === 'block' && step.name) {
                    dispatchBlockSteps(step.name, step.steps || []);
                }
            });

            // Only refresh if editor is ready and we have tabs
            if (props.editorRef.current && context.tabsRef.current.length > 0) {
                refreshCurrentTabDecorations(context.activeTabIdRef, context.tabsRef);

                if (context.activeTabIdRef.current !== 'main') {
                    refreshMainTabMetadata(context.tabsRef);
                }
            }
        };
    }

    // === ATTACH RERENDER TRIGGER ===
    // Add rerender trigger to context so hooks can call it
    useEffect(() => {
        context.triggerRerender = triggerRerender;
    }, [context, triggerRerender]);

    // === WRAPPER FUNCTIONS ===
    // Create wrapper functions that bridge between hooks and context

    /** Validate syntax of current editor content */
    const isSyntaxValidWrapper = () => {
        const code = props.editorRef.current?.getValue() ?? '';
        return isSyntaxValid(code, props.astParser);
    };

    /** Cache current tab before operations */
    const cacheCurrentTabWrapper = () => {
        context.cacheCurrentTab(
            props.editorRef,
            context.activeTabIdRef,
            context.tabsRef,
            context.tabContentRef,
            context.tabOriginalContentRef,
            getBlockSteps,
            enrichBlockSteps,
        );
    };

    /** Clear all editor decorations (simplified via context) */
    const clearAllDecorationsWrapper = () => {
        clearAllDecorations(context.activeTabIdRef, context.tabsRef);
    };

    /** Update tab modification status indicators */
    const updateCurrentTabModificationStatusWrapper = () => {
        context.updateCurrentTabModificationStatus(
            props.editorRef,
            context.activeTabIdRef,
            context.tabOriginalContentRef,
            context.setTabs,
            context.updateTabsRef,
            context.tabsRef,
        );
    };

    /** Mark tab as saved wrapper (simplified via context) */
    const markTabAsSavedWrapper = (tabKey: string) => {
        markTabAsSaved(
            tabKey,
            context.tabContentRef,
            context.tabOriginalContentRef,
            context.setTabs,
            context.updateTabsRef,
        );
    };

    // === USER INTERACTION HANDLERS ===

    /** Handle tab switching with proper state management */
    const switchTabWrapper = (tabId: string) => {
        switchTab(
            tabId,
            props.astParser,
            checkForBlockRename,
            cacheCurrentTabWrapper,
            clearAllDecorationsWrapper,
            applyTabContent,
            updateCurrentTabModificationStatusWrapper,
            updateContextMenuActions,
            dispatchBlockStepsWrapper,
            getTabInfo,
            context.getCachedTab,
        );
    };

    /** Handle tab closing with cache persistence */
    const closeTabWrapper = (tabId: string) => {
        closeTab(
            tabId,
            props.astParser,
            cacheCurrentTabWrapper,
            context.getCachedTab,
            context.setCachedTab,
            dispatchBlockStepsWrapper,
            applyTabContent,
            getTabInfo,
        );
    };

    /** Handle opening new block tab from metadata */
    const openBlockTabFromMetadataWrapper = async (metadata: FunctionMetadata) => {
        await openBlockTabFromMetadata(
            metadata,
            cacheCurrentTabWrapper,
            clearAllDecorationsWrapper,
            context.getCachedTab,
            switchTabWrapper,
        );

        // Apply decorations for the newly opened block tab
        setTimeout(() => {
            const activeTab = context.tabsRef.current.find((tab) => tab.id === context.activeTabIdRef.current);
            if (activeTab?.decorationManager && activeTab?.metadataManager) {
                const code = context.tabContentRef.current.get(context.activeTabIdRef.current) || '';

                applyDecorationsForTab(
                    activeTab,
                    code,
                    true, // init = true for newly opened tab
                    metadata.steps || [], // steps will be handled by metadata manager
                    context.blocksData || [],
                );
            }
            updateContextMenuActions();
        }, 0);
    };

    /** Editor event handling: keyboard, mouse, context menu */
    const { handleEditorKeyDown, handleEditorMouseDown, setupContextMenuActions, updateContextMenuActions } =
        useEditorEvents(openBlockTabFromMetadataWrapper);

    /** Initialize editor with decorations and event handlers */
    const onEditorReadyWrapper = () => {
        if (!props.editorRef.current || context.tabsRef.current.length === 0) return;

        // Initialize decoration manager for main tab
        const mainTab = context.tabsRef.current[0];
        if (mainTab.metadataManager) {
            mainTab.decorationManager = new DecorationManager(props.editorRef.current, mainTab.metadataManager);

            // Setup initial decorations
            applyDecorationsForTab(
                mainTab,
                props.initialCode,
                true,
                context.stepsRef.current,
                context.blocksData || [],
            );

            // === EDITOR EVENT SETUP ===

            /** Handle content changes with debouncing */
            props.editorRef.current.onDidChangeModelContent(() => {
                handleEditorChange(
                    context.activeTabIdRef,
                    context.tabsRef,
                    context.blocksData || [],
                    context.blockCacheRef,
                    updateCurrentTabModificationStatusWrapper,
                );
            });

            /** Handle keyboard interactions (execution, navigation) */
            props.editorRef.current.onKeyDown((e) =>
                handleEditorKeyDown(
                    e,
                    () => hasOneRunningStepOrOneInQueue(context.stepsRef.current, context.queueRef.current),
                    (lineNumber) => {
                        const storyInfo = getStoryInfo(context.getCachedTab);
                        if (props.executeStep) {
                            const currentSteps = storyInfo.steps;
                            const activeTab = context.tabsRef.current.find(
                                (tab) => tab.id === context.activeTabIdRef.current,
                            );
                            if (activeTab?.metadataManager) {
                                const metadata = activeTab.metadataManager.getMetadataByLineNumber(lineNumber);
                                if (metadata && !metadata.running) {
                                    const stepIndex = currentSteps.findIndex(
                                        (step: UIStep) => step.id === metadata.key,
                                    );
                                    if (stepIndex !== -1) {
                                        props.executeStep(currentSteps[stepIndex]);
                                    }
                                }
                            }
                        }
                    },
                ),
            );

            /** Handle mouse interactions (click to execute) */
            props.editorRef.current.onMouseDown((e) =>
                handleEditorMouseDown(
                    e,
                    () => hasOneRunningStepOrOneInQueue(context.stepsRef.current, context.queueRef.current),
                    (lineNumber) => {
                        const storyInfo = getStoryInfo(context.getCachedTab);
                        if (props.executeStep) {
                            const currentSteps = storyInfo.steps;
                            const activeTab = context.tabsRef.current.find(
                                (tab) => tab.id === context.activeTabIdRef.current,
                            );
                            if (activeTab?.metadataManager) {
                                const metadata = activeTab.metadataManager.getMetadataByLineNumber(lineNumber);
                                if (metadata && !metadata.running) {
                                    const stepIndex = currentSteps.findIndex(
                                        (step: UIStep) => step.id === metadata.key,
                                    );
                                    if (stepIndex !== -1) {
                                        props.executeStep(currentSteps[stepIndex]);
                                    }
                                }
                            }
                        }
                    },
                ),
            );

            /** Handle decoration manager key events */
            props.editorRef.current.onKeyDown((e) => {
                const activeTab = context.tabsRef.current.find((tab) => tab.id === context.activeTabIdRef.current);
                activeTab?.decorationManager?.onKeyDown(e);
            });

            /** Handle decoration manager paste events */
            props.editorRef.current.onDidPaste((e) => {
                const activeTab = context.tabsRef.current.find((tab) => tab.id === context.activeTabIdRef.current);
                activeTab?.decorationManager?.onDidPaste(e);
            });

            // Setup context menu actions
            setupContextMenuActions();
        }
    };

    // === IMPERATIVE API WRAPPERS ===

    /** Get story information wrapper */
    const getStoryInfoWrapper = (): StoryInfo => {
        return getStoryInfo(context.getCachedTab);
    };

    /** Get tab information wrapper */
    const getTabInfoWrapper = (tabKey: string): TabInfo => {
        return getTabInfo(tabKey, context.getCachedTab);
    };

    /** Dispatch block steps wrapper */
    const dispatchBlockStepsWrapper = (blockName: string, blockSteps: UIStep[]) => {
        return dispatchBlockSteps(blockName, blockSteps);
    };

    /** Get all modified tabs wrapper - caches current block tab first */
    const getAllModifiedTabsWrapper = (): string[] => {
        return getAllModifiedTabs(
            props.editorRef,
            context.activeTabIdRef,
            context.tabsRef,
            context.tabContentRef,
            context.tabOriginalContentRef,
            getBlockSteps,
            enrichBlockSteps,
        );
    };

    // === IMPERATIVE HANDLE ===
    // Expose API methods to parent components via ref
    useImperativeHandle(
        ref,
        () => ({
            getStoryInfo: getStoryInfoWrapper,
            getTabInfo: getTabInfoWrapper,
            isSyntaxValid: isSyntaxValidWrapper,
            onEditorReady: onEditorReadyWrapper,
            getCachedBlocks: () => context.blockCacheRef.current,
            markTabAsSaved: markTabAsSavedWrapper,
            isTabModified,
            getAllModifiedTabs: getAllModifiedTabsWrapper,
            dispatchBlockSteps: dispatchBlockStepsWrapper,
            getActiveTabId: () => context.activeTabIdRef.current,
        }),
        [],
    );

    // === RENDER ===
    return (
        <>
            {/* Tab bar (always show) */}
            <TabBar
                tabs={context.tabsRef.current}
                activeTabId={context.activeTabIdRef.current}
                onTabSwitch={switchTabWrapper}
                onTabClose={closeTabWrapper}
            />
        </>
    );
});

/**
 * TabManager - Main Component (Context Provider)
 *
 * This is the main entry point component that creates and provides the shared context.
 * It initializes the core state and passes everything to TabManagerInternal.
 *
 * Responsibilities:
 * - Initialize core state (tabs, cache, refs)
 * - Create context value with all necessary state and operations
 * - Wrap TabManagerInternal with context provider
 *
 * This separation allows:
 * - Clean context creation without circular dependencies
 * - Clear separation between state creation and consumption
 * - Easy testing of individual parts
 */
export const TabManager = forwardRef<TabManagerRef, TabManagerProps>((props, ref) => {
    // === CORE STATE INITIALIZATION ===

    /** Initialize tab state management */
    const {
        setTabs,
        setActiveTabId,
        tabsRef,
        activeTabIdRef,
        updateActiveTabId,
        updateTabsRef,
        tabContentRef,
        tabOriginalContentRef,
    } = useTabState(props.initialCode);

    /** Initialize block cache management */
    const { blockCacheRef, getCachedTab, setCachedTab, cacheCurrentTab, updateCurrentTabModificationStatus } =
        useBlockCache();

    // === REFRESH DECORATIONS FUNCTION ===
    // Ref to store the actual refresh function (will be set by TabManagerInternal)
    const refreshDecorationsRef = useRef<(() => void) | null>(null);

    // Function that calls the stored refresh function
    const refreshDecorations = useCallback(() => {
        if (refreshDecorationsRef.current) {
            refreshDecorationsRef.current();
        }
    }, []);

    // === PROP REFS FOR PERFORMANCE ===
    // Create refs for frequently changing props to avoid unnecessary re-renders
    const stepsRef = useRef(props.steps);
    const isDisabledRef = useRef(props.isDisabled || false);
    const hidePlaywrightRef = useRef(props.hidePlaywright || false);
    const variablesRef = useRef(props.variables || {});
    const queueRef = useRef(props.queue || []);

    // Single coordinated effect: update all refs first, then trigger refresh
    useEffect(() => {
        // Update all refs with latest prop values
        stepsRef.current = props.steps;
        isDisabledRef.current = props.isDisabled || false;
        hidePlaywrightRef.current = props.hidePlaywright || false;
        variablesRef.current = props.variables || {};
        queueRef.current = props.queue || [];

        // Trigger refresh after refs are updated (refs now have latest values)
        // This ensures refreshCurrentTabDecorations have access to the current ref values
        if (contextValue.refreshDecorations) {
            contextValue.refreshDecorations();
        }
    }, [props.steps, props.isDisabled, props.hidePlaywright, props.variables, props.queue]);

    // === CONTEXT VALUE CREATION ===
    // Create complete context value with all necessary state and operations
    const contextValue = {
        // Editor & References
        editorRef: props.editorRef,
        activeTabIdRef,
        tabsRef,
        tabContentRef,
        tabOriginalContentRef,
        blockCacheRef,

        // State Management
        setTabs,
        setActiveTabId,
        updateActiveTabId,
        updateTabsRef,

        // Configuration
        astParser: props.astParser,
        initialCode: props.initialCode,
        stepsRef,
        isDisabledRef,
        hidePlaywrightRef,
        variablesRef,
        queueRef,
        readOnly: props.readOnly || false,
        blocksData: props.blocksData,

        // Callbacks
        executeStep: props.executeStep,
        showSelector: props.showSelector,
        highlightSelectors: props.highlightSelectors,
        onSyntaxError: props.onSyntaxError,

        // Cache Operations
        getCachedTab,
        setCachedTab,
        cacheCurrentTab,
        updateCurrentTabModificationStatus,

        // UI Updates
        refreshDecorations,
        refreshDecorationsRef,
    };

    // === RENDER WITH CONTEXT ===
    return (
        <TabManagerProvider value={contextValue}>
            <TabManagerInternal {...props} ref={ref} />
        </TabManagerProvider>
    );
});

// Export types for external use
export type { StoryInfo, TabInfo } from './hooks/useStoryInfo';
export type { Tab } from './helpers/codeEditorHelpers';
export type { CachedTab } from './hooks/types';
